import React, { FC, useContext } from 'react';

import {
  ProjectStatus,
  statusCount,
  statusInFastFilter,
  statusInFastFilterMyInvestments,
  StatusKey,
} from 'api-client';
import { ProjectsCount } from 'api-client/models/project/ProjectsCount';
import { Image, Loader } from 'components/common';
import { FilterBar } from 'components/common/filters';
import { ProjectsContext } from 'views/dashboard/lender/investment/projects/ProjectsContext';

import styles from './ProjectsTableFilterBar.module.scss';

type Props = {
  projectsCount: ProjectsCount;
  mode?: 'default' | 'myInvestments';
  withAllStatuses?: boolean;
  onExport?: () => void;
  exportLoading?: boolean;
  exportIconSrc?: string;
};

const ALL_STATUSES_LABEL = 'Все статусы';

const getStatusCount = (projectsCount: ProjectsCount, key: string): number => {
  const countKey = statusCount[key as StatusKey];
  return countKey ? projectsCount[countKey] : 0;
};

const ProjectsTableFilterBar: FC<Props> = ({
  projectsCount,
  mode = 'default',
  withAllStatuses = true,
  onExport,
  exportLoading = false,
  exportIconSrc,
}) => {
  const context = useContext(ProjectsContext);
  const statusFilter = context.filter.statuses;
  const statusesMap: Record<string, ProjectStatus[]> =
    mode === 'myInvestments' ? statusInFastFilterMyInvestments : statusInFastFilter;

  const isAllStatusesActive = statusFilter.length === 0;

  const setStatusFilter = (statuses: ProjectStatus[]) => {
    context.setFilter({ ...context.filter, statuses });
  };

  const handleAllStatusesClick = () => {
    setStatusFilter([]);
  };

  const handleStatusClick = (statuses: ProjectStatus[]) => {
    if (!withAllStatuses) {
      setStatusFilter(statuses);
      return;
    }

    const isActive = statuses.every((status) => statusFilter.includes(status));
    setStatusFilter(isActive ? [] : statuses);
  };

  return (
    <FilterBar className={styles.filterBar}>
      <FilterBar.Pills>
        {withAllStatuses && (
          <FilterBar.Pill
            active={isAllStatusesActive}
            showClear={isAllStatusesActive}
            onClick={handleAllStatusesClick}
            onClear={handleAllStatusesClick}
          >
            {ALL_STATUSES_LABEL}
          </FilterBar.Pill>
        )}
        {Object.keys(statusesMap).map((key) => {
          const statuses = statusesMap[key];
          const isActive =
            statusFilter.length > 0 && statuses.every((status) => statusFilter.includes(status));

          return (
            <FilterBar.Pill
              key={key}
              active={isActive}
              showClear={isActive && withAllStatuses}
              onClick={() => handleStatusClick(statuses)}
              onClear={() => setStatusFilter([])}
            >
              {key} ({getStatusCount(projectsCount, key)})
            </FilterBar.Pill>
          );
        })}
      </FilterBar.Pills>
      <FilterBar.Actions>
        {onExport && exportIconSrc && (
          <button
            type="button"
            className={styles.download}
            onClick={onExport}
            aria-label="Скачать статистику"
          >
            {exportLoading ? (
              <Loader size="small" overlay={false} />
            ) : (
              <Image src={exportIconSrc} alt="" className={styles.downloadIcon} />
            )}
          </button>
        )}
        <FilterBar.Toggle
          expanded={context.filterActive}
          onClick={context.toggleFilter}
          label={context.filterActive ? 'Скрыть фильтры' : 'Показать фильтры'}
        />
      </FilterBar.Actions>
    </FilterBar>
  );
};

export default ProjectsTableFilterBar;
